import React, { useState, useEffect } from 'react';
import { getShipmentCosts, createCostBreakdown, updateCostBreakdown, deleteCostBreakdown, getContainers } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { formatCurrency } from '../lib/utils';
import { toast } from 'sonner';
import { Plus, Trash2, Edit2, DollarSign, Save, X, Calculator, FileSpreadsheet } from 'lucide-react';

// Conceptos predefinidos basados en el Excel del usuario
const COST_CONCEPTS = [
  { key: 'sea_freight', label: 'Sea Freight + Inland Freight / Flete marítimo' },
  { key: 'import_tax', label: 'Import Tax / Impuesto pedimento' },
  { key: 'port_handling', label: 'Port and local handlings / Gastos puerto, maniobras, honorarios AA' },
  { key: 'demurrage', label: 'Demourages / Demoras' },
  { key: 'local_freight_stay', label: 'Local freight stays' },
  { key: 'container_repair', label: 'Container repair' },
  { key: 'inland_freight', label: 'Inland freight / Entrega local' },
  { key: 'tax_fee', label: 'Tax fee for invoice 100% client' },
  { key: 'import_service', label: 'Import service Fee / Servicios de Importación' },
  { key: 'other', label: 'Otro' },
];

export default function CostBreakdownPanel({ mbl, isReadOnly = false }) {
  const [costs, setCosts] = useState([]);
  const [containers, setContainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingCost, setEditingCost] = useState(null);
  
  // Form state
  const [form, setForm] = useState({
    title: '',
    container_reference: '',
    items: [],
    total_mxn: null,
    visible_to_client: true,
  });
  
  // Exchange rate for calculations
  const [defaultExchangeRate, setDefaultExchangeRate] = useState(17.5);

  useEffect(() => {
    loadData();
  }, [mbl]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [costsRes, containersRes] = await Promise.all([
        getShipmentCosts(mbl),
        getContainers(mbl),
      ]);
      setCosts(costsRes.data);
      setContainers(containersRes.data);
    } catch (err) {
      console.error('Error loading costs:', err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      title: '',
      container_reference: '',
      items: COST_CONCEPTS.slice(0, 5).map(c => ({
        concept: c.label,
        amount_usd: null,
        exchange_rate: defaultExchangeRate,
        amount_mxn: null,
      })),
      total_mxn: null,
      visible_to_client: true,
    });
  };

  const handleOpenAdd = () => {
    resetForm();
    setEditingCost(null);
    setShowAddDialog(true);
  };

  const handleEdit = (cost) => {
    setForm({
      title: cost.title || '',
      container_reference: cost.container_reference || '',
      items: cost.items.length > 0 ? cost.items : [],
      total_mxn: cost.total_mxn,
      visible_to_client: cost.visible_to_client,
    });
    setEditingCost(cost);
    setShowAddDialog(true);
  };

  const handleAddRow = () => {
    setForm({
      ...form,
      items: [
        ...form.items,
        { concept: '', amount_usd: null, exchange_rate: defaultExchangeRate, amount_mxn: null },
      ],
    });
  };

  const handleRemoveRow = (index) => {
    const newItems = form.items.filter((_, i) => i !== index);
    setForm({ ...form, items: newItems });
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...form.items];
    newItems[index] = { ...newItems[index], [field]: value };
    
    // Auto-calculate MXN if USD and TC are present (round to 4 decimals,
    // Excel-style: keep up to 4 decimals of precision; the table shows only 2)
    if ((field === 'amount_usd' || field === 'exchange_rate') && newItems[index].amount_usd && newItems[index].exchange_rate) {
      newItems[index].amount_mxn = Math.round(parseFloat(newItems[index].amount_usd) * parseFloat(newItems[index].exchange_rate) * 10000) / 10000;
    }
    
    setForm({ ...form, items: newItems });
  };

  const calculateTotal = () => {
    const total = form.items.reduce((sum, item) => {
      return sum + (parseFloat(item.amount_mxn) || 0);
    }, 0);
    setForm({ ...form, total_mxn: Math.round(total * 10000) / 10000 });
  };

  const handleSave = async () => {
    // Validate
    if (form.items.length === 0) {
      toast.error('Agrega al menos un concepto de costo');
      return;
    }

    try {
      const payload = {
        title: form.title,
        container_reference: form.container_reference,
        items: form.items.filter(item => item.concept),
        total_mxn: Math.round((form.total_mxn || form.items.reduce((sum, item) => sum + (parseFloat(item.amount_mxn) || 0), 0)) * 10000) / 10000,
        visible_to_client: form.visible_to_client,
      };

      if (editingCost) {
        await updateCostBreakdown(mbl, editingCost.id, payload);
        toast.success('Desglose actualizado');
      } else {
        await createCostBreakdown(mbl, payload);
        toast.success('Desglose creado');
      }
      
      setShowAddDialog(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (costId) => {
    if (!window.confirm('¿Eliminar este desglose de costos?')) return;
    
    try {
      await deleteCostBreakdown(mbl, costId);
      toast.success('Desglose eliminado');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-muted-foreground">Cargando costos...</div>;
  }

  return (
    <div className="space-y-4" data-testid="cost-breakdown-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg flex items-center gap-2">
          <DollarSign className="w-5 h-5" />
          Desglose de Costos
        </h3>
        {!isReadOnly && (
          <Button onClick={handleOpenAdd} size="sm" data-testid="add-cost-btn">
            <Plus size={16} className="mr-1" />
            Nuevo Desglose
          </Button>
        )}
      </div>

      {/* Cost breakdowns list */}
      {costs.length === 0 ? (
        <div className="bg-card border border-border rounded-sm p-8 text-center">
          <FileSpreadsheet className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">No hay desgloses de costos registrados</p>
          {!isReadOnly && (
            <Button variant="outline" className="mt-4" onClick={handleOpenAdd}>
              <Plus size={16} className="mr-1" />
              Agregar Desglose
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {costs.map((cost) => (
            <div key={cost.id} className="bg-card border border-border rounded-sm overflow-hidden" data-testid="cost-breakdown-card">
              {/* Card Header */}
              <div className="bg-secondary/50 px-4 py-3 flex items-center justify-between">
                <div>
                  <h4 className="font-medium">{cost.title || 'Desglose de Costos'}</h4>
                  {cost.container_reference && (
                    <p className="text-sm text-muted-foreground">{cost.container_reference}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {!cost.visible_to_client && (
                    <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded">
                      Solo interno
                    </span>
                  )}
                  {!isReadOnly && (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(cost)}>
                        <Edit2 size={14} />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(cost.id)}>
                        <Trash2 size={14} />
                      </Button>
                    </>
                  )}
                </div>
              </div>
              
              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border text-sm text-muted-foreground">
                      <th className="text-left p-3 font-medium">Concepto</th>
                      <th className="text-right p-3 font-medium w-24">USD</th>
                      <th className="text-right p-3 font-medium w-20">TC</th>
                      <th className="text-right p-3 font-medium w-28">MXP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cost.items.map((item, idx) => (
                      <tr key={idx} className="border-b border-border/50 text-sm">
                        <td className="p-3">{item.concept}</td>
                        <td className="p-3 text-right font-mono">
                          {item.amount_usd ? formatCurrency(item.amount_usd, 'USD') : '—'}
                        </td>
                        <td className="p-3 text-right font-mono text-muted-foreground">
                          {item.exchange_rate || '—'}
                        </td>
                        <td className="p-3 text-right font-mono">
                          {item.amount_mxn ? formatCurrency(item.amount_mxn, 'MXN') : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-secondary/30 font-medium">
                      <td className="p-3" colSpan={3}>Total Cost</td>
                      <td className="p-3 text-right font-mono text-lg text-primary">
                        {cost.total_mxn ? formatCurrency(cost.total_mxn, 'MXN') : '—'}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingCost ? 'Editar Desglose de Costos' : 'Nuevo Desglose de Costos'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Header fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Título / Referencia</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Ej: Resumen importación EXTENSIONES"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Contenedor / Referencia</Label>
                <Select 
                  value={form.container_reference || 'general'} 
                  onValueChange={(v) => setForm({ ...form, container_reference: v === 'general' ? '' : v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Seleccionar contenedor (opcional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General (sin contenedor específico)</SelectItem>
                    {containers.map(c => (
                      <SelectItem key={c.id} value={c.container_number}>
                        {c.container_number}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Default Exchange Rate */}
            <div className="flex items-center gap-4">
              <Label>Tipo de Cambio por defecto:</Label>
              <Input
                type="number"
                step="0.0001"
                value={defaultExchangeRate}
                onChange={(e) => setDefaultExchangeRate(parseFloat(e.target.value) || 17.5)}
                className="w-24"
              />
            </div>

            {/* Items table */}
            <div className="border border-border rounded-sm overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-secondary/50 text-sm">
                    <th className="text-left p-2 font-medium">Concepto</th>
                    <th className="text-right p-2 font-medium w-28">USD</th>
                    <th className="text-right p-2 font-medium w-20">TC</th>
                    <th className="text-right p-2 font-medium w-32">MXP</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {form.items.map((item, idx) => (
                    <tr key={idx} className="border-t border-border/50">
                      <td className="p-2">
                        <Select 
                          value={item.concept} 
                          onValueChange={(v) => handleItemChange(idx, 'concept', v)}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Seleccionar concepto" />
                          </SelectTrigger>
                          <SelectContent>
                            {COST_CONCEPTS.map(c => (
                              <SelectItem key={c.key} value={c.label}>{c.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-2">
                        <Input
                          type="number"
                          step="0.0001"
                          value={item.amount_usd || ''}
                          onChange={(e) => handleItemChange(idx, 'amount_usd', e.target.value)}
                          placeholder="0.00"
                          className="text-right font-mono"
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          type="number"
                          step="0.0001"
                          value={item.exchange_rate || ''}
                          onChange={(e) => handleItemChange(idx, 'exchange_rate', e.target.value)}
                          placeholder="17.5"
                          className="text-right font-mono w-20"
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          type="number"
                          step="0.0001"
                          value={item.amount_mxn || ''}
                          onChange={(e) => handleItemChange(idx, 'amount_mxn', e.target.value)}
                          placeholder="0.00"
                          className="text-right font-mono"
                        />
                      </td>
                      <td className="p-2">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-destructive"
                          onClick={() => handleRemoveRow(idx)}
                        >
                          <X size={14} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-border bg-secondary/30">
                    <td className="p-2" colSpan={3}>
                      <Button variant="outline" size="sm" onClick={handleAddRow}>
                        <Plus size={14} className="mr-1" />
                        Agregar Concepto
                      </Button>
                    </td>
                    <td className="p-2">
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          step="0.0001"
                          value={form.total_mxn || ''}
                          onChange={(e) => setForm({ ...form, total_mxn: parseFloat(e.target.value) })}
                          placeholder="Total"
                          className="text-right font-mono font-bold"
                        />
                        <Button variant="ghost" size="sm" onClick={calculateTotal} title="Calcular total">
                          <Calculator size={14} />
                        </Button>
                      </div>
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Visibility toggle */}
            <div className="flex items-center gap-3 pt-2">
              <Switch
                checked={form.visible_to_client}
                onCheckedChange={(v) => setForm({ ...form, visible_to_client: v })}
                id="visible-to-client"
              />
              <Label htmlFor="visible-to-client">Visible para el cliente</Label>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-border">
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSave} data-testid="save-cost-btn">
                <Save size={16} className="mr-1" />
                {editingCost ? 'Actualizar' : 'Guardar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
